/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * «Buscar código» cuando la sucursal no tiene cámara habilitada (web/hardware
 * wedge): el cajero escribe el código y confirma. El submit reutiliza el mismo
 * path de `handleScannerResult` que una lectura de cámara o de lector HID.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { styles } from '../posShared.styles';
import { ModalShell } from '../../../shared/modals/ModalShell';
import { ModalFormField } from '../../../shared/modals/ModalFormField';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { colors } from '../../../shared/theme/colors';

type PosManualBarcodeModalProps = {
  visible: boolean;
  onClose: () => void;
  /** Mismo contrato que `BarcodeScannerModal.onCodeScanned`. */
  onCodeScanned: (code: string) => void;
};

const MAX_CODE_LENGTH = 64;

export function PosManualBarcodeModal({
  visible,
  onClose,
  onCodeScanned,
}: PosManualBarcodeModalProps) {
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft('');
    }
  }, [visible]);

  const handleSubmit = useCallback(() => {
    const code = draft.trim();
    if (!code) return;
    onCodeScanned(code);
  }, [draft, onCodeScanned]);

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      onRequestClose={onClose}
      title="Buscar código"
      subtitle="Escribí el código del producto y confirmá."
      closeAccessibilityLabel="Cerrar búsqueda por código"
      scrollable={false}
      footer={
        <View style={[styles.modalActions, modalExperience.footerActions]}>
          <TouchableOpacity
            style={[styles.secondaryButton, { flex: 1 }]}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cancelar búsqueda por código"
          >
            <Text style={styles.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.posPrimaryCta, { flex: 1 }]}
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel="Buscar código ingresado"
            testID="pos.barcode.manual.submit"
          >
            <Text style={styles.posPrimaryCtaText}>Buscar</Text>
          </TouchableOpacity>
        </View>
      }
    >
      <View style={modalExperience.formPanel}>
        <ModalFormField label="Código">
          <TextInput
            value={draft}
            onChangeText={setDraft}
            placeholder="Ej: 7791234567890"
            placeholderTextColor={colors.textMuted}
            style={modalExperience.formInput}
            autoFocus
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={MAX_CODE_LENGTH}
            returnKeyType="search"
            onSubmitEditing={handleSubmit}
            blurOnSubmit={false}
            accessibilityLabel="Código de barras"
            testID="pos.barcode.manual.input"
          />
        </ModalFormField>
      </View>
    </ModalShell>
  );
}
